// Pure BOGO / Mix&Match decision helpers — faithful ports of the Android
// `UtilsMethodExtension.kt` sibling helpers (each has its own unit-test class there):
//   cappedBogoSets · freshBogoFires · bogoCapBinds · isBrokenPreservedBogoGroup ·
//   cappedMixMatchUnits · mixMatchDiscountAllocation
//
// They are deliberately tiny, pure and side-effect free so the money DECISIONS can be pinned by
// tests independently of the cart machinery, exactly as Android does.

import { toNum } from "./money";

// Android `String?.toIntOrNull()`: null / blank / non-numeric → null. A decimal string ("2.5") is
// NOT an Int in Kotlin and yields null, so it must yield null here too — parseInt would say 2.
function toIntOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!/^[+-]?\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * How many BOGO sets may fire: floor(eligibleSize / buyQty), clamped to the per-order limit.
 * `limitRaw` null / blank / non-numeric / 0 / negative = UNLIMITED.
 */
export function cappedBogoSets(eligibleSize, buyQty, limitRaw) {
  if (buyQty <= 0) return 0;
  const possible = Math.floor(eligibleSize / buyQty);
  const limit = toIntOrNull(limitRaw) ?? 0;
  return limit <= 0 ? possible : Math.min(possible, limit);
}

/**
 * Preserve-aware fire count — how many FRESH sets may fire given the per-order limit and the fires
 * already preserved this order for the deal (restored + committed sets). Counting preserved and
 * fresh TOGETHER is what stops restoring a capped order from re-granting suppressed fires.
 *
 * ⚠️ Never route a REDUCED limit back through cappedBogoSets: its "0 means unlimited" convention
 * would turn an exhausted cap into "unlimited" (an Android unit test caught exactly that).
 */
export function freshBogoFires(eligibleSize, buyQty, rawLimit, preservedFires) {
  const possible = cappedBogoSets(eligibleSize, buyQty, null); // floor(size/buyQty); buyQty<=0 → 0
  const rawCap = toIntOrNull(rawLimit) ?? 0;
  if (rawCap <= 0) return possible; // unlimited
  return Math.min(possible, Math.max(0, rawCap - toNum(preservedFires)));
}

/**
 * Has the per-order limit BOUND (a real finite limit exists and preserved + fresh fires reach it)?
 *
 * Since the commit-lock was retired (Android 2026-08-08) this no longer freezes anything — it gates
 * the promo HINT only: an exhausted deal must stop telling the cashier "add one more and it fires".
 * UNLIMITED deals never bind.
 */
export function bogoCapBinds(preservedFires, totalSets, rawLimit) {
  const rawCap = toIntOrNull(rawLimit) ?? 0;
  return rawCap > 0 && toNum(preservedFires) + toNum(totalSets) >= rawCap;
}

/**
 * BOGO discounted-unit SELECTION — Android `selectBogoDiscountedIndices` (2026-08-08, Interpretation
 * A). Given the ELIGIBLE unit prices IN ADD-ORDER, returns the indices that get the discount.
 *
 * OWNER RULE: a deal "set" is `buyQty` units — "Buy 5 Get 2" means *within every 5 units, the 2
 * cheapest are discounted*, NOT 5 paid plus 2 free. Within each fired set the `min(freeQty, buyQty)`
 * CHEAPEST members are discounted; price ties break to the earliest index (= earliest `orderId`).
 * The `min()` caps the oddball "get more than you buy" deal (Buy 2 Get 5 ⇒ a 2-unit set, both
 * discounted).
 *
 * ⚠️ MEMBERSHIP IS ADD-ORDER, AND THAT IS THE WHOLE POINT. Sets are contiguous index blocks
 * `[s*buyQty, (s+1)*buyQty)` over the add-ordered list, so a later-added unit — even a cheaper one —
 * always lands in a LATER set and can never steal an already-fired set's slot. That is what fixed the
 * reported shift bug (a $10 added after a $20 set had fired stole the discount, and the FREE badge
 * visibly moved), and it is why no mutable commit-lock is needed any more: add-order is inherently
 * sticky for adds, and deletes simply re-derive.
 *
 * ⚠️ Add-order therefore CHANGES THE MONEY — that is accepted and intended; add-order independence
 * is explicitly NOT a requirement of this deal model.
 *
 * Guards: `buyQty <= 0 || freeQty <= 0` ⇒ empty (malformed / no-op deal).
 */
export function selectBogoDiscountedIndices(pricesInAddOrder, buyQty, freeQty, rawLimit, preservedFires = 0) {
  const prices = Array.isArray(pricesInAddOrder) ? pricesInAddOrder : [];
  const result = new Set();
  if (buyQty <= 0 || freeQty <= 0) return result;
  const discountPerSet = Math.min(freeQty, buyQty); // can't discount more units than the set holds
  const fires = freshBogoFires(prices.length, buyQty, rawLimit, preservedFires);
  for (let s = 0; s < fires; s++) {
    const start = s * buyQty;
    const end = (s + 1) * buyQty;
    if (end > prices.length) break; // defensive; floor(N/buyQty) guarantees full sets
    const members = [];
    for (let i = start; i < end; i++) members.push(i);
    // cheapest first WITHIN this set; equal prices keep add-order (earliest index wins).
    members.sort((a, b) => toNum(prices[a]) - toNum(prices[b]) || a - b);
    for (const i of members.slice(0, discountPerSet)) result.add(i);
  }
  return result;
}

/**
 * Is a preserved (restored/committed) BOGO group no longer supported by enough paid partners?
 * All members of a restored group share ONE identifier and a group may span several fired sets, so
 * the test is free-line-based: preservedFires = freeLines / freeQty, requiredMembers =
 * preservedFires × buyQty, broken = memberCount < requiredMembers. Deleting a paid partner therefore
 * unfreezes the group instead of stranding a lone free line at $0 (an undercharge).
 */
export function isBrokenPreservedBogoGroup(memberCount, freeLineCount, buyQty, freeQty) {
  if (buyQty <= 0) return false;
  const fq = freeQty > 0 ? freeQty : 1;
  const preservedFires = Math.floor(freeLineCount / fq);
  return memberCount < preservedFires * buyQty;
}

/**
 * Mix & Match per-order UNIT cap (`max_qty`): how many eligible units get the bulk price.
 * null / blank / non-numeric / 0 / negative = NO cap (discount every eligible unit).
 */
export function cappedMixMatchUnits(totalQty, capRaw) {
  const cap = toIntOrNull(capRaw) ?? 0;
  return cap <= 0 ? totalQty : Math.min(totalQty, cap);
}

/**
 * Per-line unit allocation for the Mix & Match cap. `orderCounts` is each eligible line's unit count
 * IN THE CALLER'S CHOSEN ORDER (earliest-added first — see validateBulkPrice); returns how many of
 * each line's units get the bulk price. A value strictly between 0 and the line's own count means
 * that line STRADDLES the cap and must be split.
 */
export function mixMatchDiscountAllocation(orderCounts, unitsToDiscount) {
  const alloc = new Array(orderCounts.length).fill(0);
  let remaining = toNum(unitsToDiscount);
  for (let i = 0; i < orderCounts.length; i++) {
    const give = Math.min(toNum(orderCounts[i]), Math.max(0, remaining));
    alloc[i] = give;
    remaining -= give;
  }
  return alloc;
}
